<template>
    <kgr-accordion-item
        :title="$t('Angebotsartikel')"
        :open="openByDefault"
    >
        <div class="kgr-row kgr-row--extraSmallMargin">
            {{ $t('offering.salesListOfferingItems.hint') }}
        </div>
        <div class="search-container mb1">
            <input
                v-model="search"
                type="text"
                class="kgr-input search"
                :placeholder="$t('search')"
            />
            <kgr-icon
                v-show="search.length > 0"
                icon-class="close"
                class="search-clear"
                @click="clearSearch()"
            />
        </div>
        <table
            vue:is="kgr-table"
            class="kgr-table kgr-table--striped kgr-table--block"
            :is-data-table="false"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('Name') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('Kategorie') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ $t('Preis') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >{{ $t('Verfügbarkeit') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td colspan="4">
                        <kgr-loading inline />
                    </td>
                </tr>
                <tr
                    v-else
                    v-for="item in filterOfferingItems"
                    :key="item.id"
                    class="kgr-table_row"
                >
                    <td class="kgr-table__cell">
                        {{ item.name }}
                    </td>
                    <td class="kgr-table__cell">
                        {{ item.category.name }}
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-price
                            :price="item.pricePerTime"
                            with-vat
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-checkbox
                            :checked="isSelected(item)"
                            @change="addOrRemove(item)"
                            :readonly="!isEditable"
                            :title="noPermissionTitle"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </kgr-accordion-item>
</template>

<script>
import AccordionItem from '../common/accordion/AccordionItem.vue';
import Checkbox from '../common/form/checkbox/Checkbox.vue';
import ClubSalesListOfferingItemRepository from '../../js/repository/club/ClubSalesListOfferingItemRepository.js';
import EventBus from '../../js/service/EventBus.js';
import Icon from '../common/icon/Icon.vue';
import Loading from '../common/Loading.vue';
import Offering from '../../js/dto/offering/Offering.js';
import PricePerTimeOutput from '../club/sales-list/PricePerTimeOutput.vue';

export default {
    components: {
        'kgr-accordion-item': AccordionItem,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-price': PricePerTimeOutput,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            loading: false,
            offeringItems: [],
            selectedSalesListOfferingItems: [],
            openByDefault: this.offering.salesListOfferingItems.length > 0 && this.isEditable,
            search: '',
        }
    },
    computed: {
        searchString() {
            return this.search.trim().toLowerCase();
        },
        filterOfferingItems() {
            if (!this.searchString) {
                return this.offeringItems;
            }
            return this.offeringItems.filter(item => {
                return item.name.toLowerCase().includes(this.searchString)
                    || item.category.name.toLowerCase().includes(this.searchString);
            });
        },
        noPermissionTitle() {
            return this.isEditable ? '' : this.$t('Für diese Aktion fehlt dir die notwendige Berechtigung. Für mehr Informationen wende dich an einen Admin.');
        },
    },
    methods: {
        loadOfferingItems() {
            this.loading = true;
            ClubSalesListOfferingItemRepository.findByClubId(this.clubId)
                .then(items => {
                    this.offeringItems = items;
                    this.selectOfferingItemsByOffering();
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        isSelected(item) {
            return this.selectedSalesListOfferingItems.includes(item);
        },
        addOrRemove(item) {
            if (!this.selectedSalesListOfferingItems.includes(item)) {
                this.selectedSalesListOfferingItems.push(item);
            } else {
                const index = this.selectedSalesListOfferingItems.indexOf(item);
                if (index !== -1) {
                    this.selectedSalesListOfferingItems.splice(index, 1);
                }
            }
            this.$emit('selected', this.selectedSalesListOfferingItems);
        },
        selectOfferingItemsByOffering() {
            this.selectedSalesListOfferingItems = this.offeringItems.filter(item => {
                return this.offering.salesListOfferingItems.some(selectedItem => selectedItem.clubFeeId === item.id);
            });
            this.$emit('selected', this.selectedSalesListOfferingItems);
        },
        clearSearch() {
            this.search = '';
        },
    },
    created() {
        this.loadOfferingItems();
        EventBus.$on('offering-updated', () => this.clearSearch());
    },
    beforeUnmount() {
        EventBus.$off('offering-updated');
    }
}
</script>

<style scoped lang="scss">
.search-container {
    position: relative;
    display: inline-block;
    margin-bottom: 1rem;
}

.search-clear {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
